/* eslint-disable sonarjs/no-identical-functions */

import * as React from "react";

import { EmailCategory, getDatePlusNHours } from "@simplyk/common";
import { useSnackbar } from "notistack";

import { DateEmailScheduler } from "@/components/EmailSchedulerDialog/DateEmailScheduler";
import { AmplitudeEvents } from "@/constants/amplitude";
import { trpc } from "@/helpers/trpc";
import { useAmplitude } from "@/hooks/amplitude/useAmplitude";
import { useTranslate } from "@/hooks/useTranslate";
import { TicketingFormEditionContext } from "@/src/providers/TicketingFormEditionProvider";

type BasicEmailSchedulerProps = {
  emailContentId: string;
  emailSchedulerCategory: EmailCategory;
  closeEmailScheduler: () => void;
  closeEmailEditor: () => void;
  refetch?: () => void;
  onSubmit?: () => void;
  isSchedulerOpen: boolean;
};

export const BasicEmailScheduler = ({
  emailContentId,
  emailSchedulerCategory,
  closeEmailScheduler,
  closeEmailEditor,
  refetch,
  onSubmit,
  isSchedulerOpen,
}: BasicEmailSchedulerProps) => {
  const { t } = useTranslate();
  const { logAmplitudeEvent } = useAmplitude();

  const {
    data: { id: ticketingId },
  } = React.useContext(TicketingFormEditionContext);
  const { enqueueSnackbar } = useSnackbar();

  const { mutateAsync: scheduleEmailContentMutation } = trpc.scheduleEmailContent.useMutation();
  const { mutateAsync: unscheduleEmailContentMutation } = trpc.unscheduleEmailContent.useMutation();

  const {
    data: emailContent,
    refetch: refetchEmailDelivery,
    isLoading,
  } = trpc.getEmailContent.useQuery({ emailContentId: emailContentId as string }, { enabled: Boolean(emailContentId) });

  const scheduledFor = React.useMemo(
    () => (emailContent?.emailContent.scheduledFor ? new Date(emailContent.emailContent.scheduledFor) : undefined),
    [emailContent]
  );

  const handleRefetch = React.useCallback(() => {
    refetch?.();
    if (emailContentId) {
      refetchEmailDelivery?.();
    }
  }, [emailContentId, refetch, refetchEmailDelivery]);

  const handleSchedulerSubmit = React.useCallback(
    async (scheduledFor: Date) => {
      if (!emailContentId) {
        return;
      }

      const { data } = await scheduleEmailContentMutation({
        emailContentId,
        scheduledFor,
      });
      if (data) {
        handleRefetch();
        enqueueSnackbar(t("dashboard", "EmailEditor.scheduler.schedule.success"), {
          vibe: "positive",
        });
        logAmplitudeEvent(AmplitudeEvents.EmailScheduleForLaterCompleted, {
          category: "basic",
          ticketingId,
          emailContentId,
          scheduledFor,
        });
      } else {
        enqueueSnackbar(t("dashboard", "EmailEditor.scheduler.schedule.error"), {
          vibe: "danger",
        });
      }

      closeEmailScheduler();
      closeEmailEditor();
      onSubmit?.();
    },
    [
      emailContentId,
      scheduleEmailContentMutation,
      closeEmailScheduler,
      closeEmailEditor,
      handleRefetch,
      enqueueSnackbar,
      t,
      logAmplitudeEvent,
      ticketingId,
      onSubmit,
    ]
  );

  const handleSchedulerCancel = React.useCallback(async () => {
    if (scheduledFor) {
      const { error } = await unscheduleEmailContentMutation({
        emailContentId,
      });
      if (!error) {
        handleRefetch();
        enqueueSnackbar(t("dashboard", "EmailEditor.scheduler.unschedule.success"), {
          vibe: "positive",
        });
        logAmplitudeEvent(AmplitudeEvents.EmailUnscheduled, {
          category: "basic",
          ticketingId,
          emailContentId,
        });
        closeEmailEditor();
      } else {
        enqueueSnackbar(t("dashboard", "EmailEditor.scheduler.unschedule.error"), {
          vibe: "danger",
        });
      }
    }

    closeEmailScheduler();
  }, [
    scheduledFor,
    closeEmailScheduler,
    unscheduleEmailContentMutation,
    emailContentId,
    handleRefetch,
    enqueueSnackbar,
    t,
    logAmplitudeEvent,
    ticketingId,
    closeEmailEditor,
  ]);

  if (isLoading) {
    return null;
  }

  return (
    <DateEmailScheduler
      isOpen={isSchedulerOpen && emailSchedulerCategory === "basic"}
      title={t("dashboard", "EmailEditor.scheduler.title.basic")}
      scheduledFor={scheduledFor || getDatePlusNHours(new Date(), 1)}
      cancelLabel={scheduledFor ? t("dashboard", "EmailScheduler.unschedule") : t("dashboard", "EmailScheduler.cancel")}
      onSubmit={handleSchedulerSubmit}
      onCancel={handleSchedulerCancel}
      onClose={closeEmailScheduler}
    />
  );
};
